import { useState, useEffect, useMemo } from 'react'
import { Target, Plus, ChevronRight, ChevronDown, Calendar, Briefcase, Heart, User, BookOpen, DollarSign, Users, MoreHorizontal, Edit, Trash2, Link2, ArrowLeft, TrendingUp, Repeat, CheckSquare, FolderKanban, Eye, Flame, Circle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useGoals } from '@/hooks/useGoals'
import { useProjects } from '@/hooks/useProjects'
import { useTasks } from '@/hooks/useTasks'
import { AttachedItemsSection } from '@/components/AttachedItemsSection'
import { useApp } from '@/context/AppContext'
import type { Goal, CreateGoalInput, TimeHorizon, GoalCategory, GoalStatus } from '@/types'
import type { GoalProgress } from '@/hooks/useGoals'
import type { HabitInstance, PacingStatus } from '@trackmind/core'
import { habitInstancesFor, habitRecord } from '@trackmind/core'
import { useToday } from '@/hooks/useToday'
import { cn, formatDate } from '@/lib/utils'

interface GoalsViewProps {
  onBack?: () => void
}

const CATEGORY_ICONS: Record<GoalCategory, typeof Briefcase> = {
  career: Briefcase,
  health: Heart,
  personal: User,
  learning: BookOpen,
  financial: DollarSign,
  relationships: Users,
  other: MoreHorizontal,
}

const CATEGORY_COLORS: Record<GoalCategory, string> = {
  career: 'text-blue-500',
  health: 'text-green-500',
  personal: 'text-purple-500',
  learning: 'text-amber-500',
  financial: 'text-emerald-500',
  relationships: 'text-pink-500',
  other: 'text-gray-500',
}

const HORIZON_LABELS: Record<TimeHorizon, string> = {
  yearly: 'Yearly',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
}

const STATUS_COLORS: Record<GoalStatus, string> = {
  active: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  achieved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  abandoned: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
}

/**
 * Progress trend from check-in history. Inline SVG rather than a chart library —
 * it's a handful of points, and this keeps it dependency-free and legible at
 * phone width.
 */
function ProgressSparkline({ checkIns }: { checkIns: { date: string; progress: number }[] }) {
  if (checkIns.length < 2) return null
  const W = 100, H = 28
  const pts = checkIns.map((c, i) => {
    const x = (i / (checkIns.length - 1)) * W
    const y = H - (Math.max(0, Math.min(100, c.progress)) / 100) * H
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  const first = checkIns[0].progress
  const last = checkIns[checkIns.length - 1].progress
  const delta = last - first

  return (
    <div className="flex items-center gap-2">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-7 flex-1 min-w-0 overflow-visible"
        role="img"
        aria-label={`Progress trend: ${first}% to ${last}%`}
      >
        <polyline
          points={pts.join(' ')}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
          className="text-primary"
        />
      </svg>
      <span className={cn(
        'text-xs tabular-nums shrink-0',
        delta > 0 ? 'text-green-600 dark:text-green-400'
          : delta < 0 ? 'text-amber-600 dark:text-amber-400'
          : 'text-muted-foreground'
      )}>
        {delta > 0 ? '+' : ''}{delta}pt
      </span>
    </div>
  )
}

// Pacing = derived progress vs. share of the time window already spent.
// For a year-long goal this is the number that actually tells you something.
const PACING_LABELS: Record<PacingStatus, string> = {
  ahead: 'Ahead',
  'on-track': 'On track',
  behind: 'Behind',
  'no-target': '',
}

const PACING_COLORS: Record<PacingStatus, string> = {
  ahead: 'border-green-500 text-green-600 dark:text-green-400',
  'on-track': 'border-blue-500 text-blue-600 dark:text-blue-400',
  behind: 'border-amber-500 text-amber-600 dark:text-amber-400',
  'no-target': '',
}

export function GoalsView({ onBack }: GoalsViewProps) {
  const { goals, yearlyGoals, quarterlyGoals, monthlyGoals, activeGoals, createGoal, updateGoal, deleteGoal, linkProject, unlinkProject, linkHabit, unlinkHabit, linkTask, unlinkTask, progressById, addKeyResult, stepKeyResult, deleteKeyResult, addCheckIn, deleteCheckIn } = useGoals()
  const { activeProjects } = useProjects()
  const { habits, regularTasks } = useTasks()

  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  const [showVisionBoard, setShowVisionBoard] = useState(false)
  const [linkDialogTab, setLinkDialogTab] = useState<'projects' | 'habits' | 'tasks'>('projects')
  const [expandedHorizons, setExpandedHorizons] = useState<Set<TimeHorizon>>(new Set(['yearly', 'quarterly', 'monthly']))
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set())

  // Form state
  const [formTitle, setFormTitle] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formHorizon, setFormHorizon] = useState<TimeHorizon>('quarterly')
  const [formCategory, setFormCategory] = useState<GoalCategory>('personal')
  const [formTargetDate, setFormTargetDate] = useState('')
  const [formParentId, setFormParentId] = useState<string>('__none__')
  const [formOverride, setFormOverride] = useState<string>('')
  // Key-result add dialog
  const [krGoalId, setKrGoalId] = useState<string | null>(null)
  const [krLabel, setKrLabel] = useState('')
  const [krTarget, setKrTarget] = useState('1')
  const [krUnit, setKrUnit] = useState('')
  // Check-in dialog
  const [checkInGoalId, setCheckInGoalId] = useState<string | null>(null)
  const [checkInNote, setCheckInNote] = useState('')
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null)

  // Progress is derived synchronously from the goal/task/project graph, so the
  // detail panel just reads it — no loading state, no refetch, and it updates
  // the moment a linked task is completed.
  const goalProgress = selectedGoal ? progressById[selectedGoal.id] ?? null : null

  // Drives whether the detail panel is a side panel or a bottom sheet. Matches
  // Tailwind's `md` breakpoint so the two presentations never both mount.
  const [isPhone, setIsPhone] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const onChange = (e: MediaQueryListEvent) => setIsPhone(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Keep the selected goal object in sync with the store after an edit.
  useEffect(() => {
    if (!selectedGoal) return
    const fresh = goals.find(g => g.id === selectedGoal.id)
    if (fresh && fresh !== selectedGoal) setSelectedGoal(fresh)
    if (!fresh) setSelectedGoal(null)
  }, [goals, selectedGoal])


  const handleCreateGoal = async () => {
    if (!formTitle.trim()) return
    const data: CreateGoalInput = {
      title: formTitle,
      description: formDescription || undefined,
      time_horizon: formHorizon,
      category: formCategory,
      target_date: formTargetDate || undefined,
      parent_goal_id: formParentId && formParentId !== '__none__' ? formParentId : undefined,
    }
    await createGoal(data)
    resetForm()
    setShowCreateDialog(false)
  }

  const handleUpdateGoal = async () => {
    if (!editingGoal || !formTitle.trim()) return
    await updateGoal(editingGoal.id, {
      title: formTitle,
      description: formDescription || undefined,
      time_horizon: formHorizon,
      category: formCategory,
      target_date: formTargetDate || undefined,
      parent_goal_id: formParentId && formParentId !== '__none__' ? formParentId : undefined,
      progress_override: formOverride === '' ? null : Math.max(0, Math.min(100, Number(formOverride))),
    })
    resetForm()
    setEditingGoal(null)
  }

  const handleDeleteGoal = async (goal: Goal) => {
    if (!confirm(`Delete goal "${goal.title}"?`)) return
    await deleteGoal(goal.id)
    if (selectedGoal?.id === goal.id) {
      setSelectedGoal(null)
    }
  }

  const handleLinkProject = async (projectId: string) => {
    if (!selectedGoal) return
    await linkProject(selectedGoal.id, projectId)
  }

  const handleUnlinkProject = async (projectId: string) => {
    if (!selectedGoal) return
    await unlinkProject(selectedGoal.id, projectId)
  }

  const handleLinkHabit = async (habitId: string) => {
    if (!selectedGoal) return
    await linkHabit(selectedGoal.id, habitId)
  }

  const handleUnlinkHabit = async (habitId: string) => {
    if (!selectedGoal) return
    await unlinkHabit(selectedGoal.id, habitId)
  }

  const handleLinkTask = async (taskId: string) => {
    if (!selectedGoal) return
    await linkTask(selectedGoal.id, taskId)
  }

  const handleUnlinkTask = async (taskId: string) => {
    if (!selectedGoal) return
    await unlinkTask(selectedGoal.id, taskId)
  }

  const handleStatusChange = async (goal: Goal, status: GoalStatus) => {
    // updateGoal stamps achieved_at when crossing into 'achieved'
    await updateGoal(goal.id, { status })
  }

  const startEdit = (goal: Goal) => {
    setEditingGoal(goal)
    setFormTitle(goal.title)
    setFormDescription(goal.description || '')
    setFormHorizon(goal.time_horizon)
    setFormCategory(goal.category)
    setFormTargetDate(goal.target_date || '')
    setFormParentId(goal.parent_goal_id || '__none__')
    setFormOverride(
      goal.progress_override === null || goal.progress_override === undefined
        ? ''
        : String(goal.progress_override)
    )
  }

  const resetForm = () => {
    setFormTitle('')
    setFormDescription('')
    setFormHorizon('quarterly')
    setFormCategory('personal')
    setFormTargetDate('')
    setFormParentId('__none__')
    setFormOverride('')
    setEditingGoal(null)
  }

  const toggleHorizon = (horizon: TimeHorizon) => {
    setExpandedHorizons((prev) => {
      const next = new Set(prev)
      if (next.has(horizon)) {
        next.delete(horizon)
      } else {
        next.add(horizon)
      }
      return next
    })
  }

  const toggleGoalExpand = (goalId: string) => {
    setExpandedGoals((prev) => {
      const next = new Set(prev)
      if (next.has(goalId)) {
        next.delete(goalId)
      } else {
        next.add(goalId)
      }
      return next
    })
  }

  const getChildGoals = (parentId: string) => goals.filter((g) => g.parent_goal_id === parentId)


  const renderGoalCard = (goal: Goal, depth: number = 0) => {
    const CategoryIcon = CATEGORY_ICONS[goal.category]
    const childGoals = getChildGoals(goal.id)
    const hasChildren = childGoals.length > 0
    const isExpanded = expandedGoals.has(goal.id)
    // Derived, rolled-up progress — includes sub-goals, projects, tasks, habits
    const cardProgress = progressById[goal.id]
    const cardPct = cardProgress?.progress ?? 0

    return (
      <div key={goal.id} className={cn("space-y-2", depth > 0 && "ml-6 mt-2")}>
        <Card
          className={cn(
            "cursor-pointer hover:shadow-md transition-all hover:border-primary/50",
            selectedGoal?.id === goal.id && "border-primary ring-1 ring-primary"
          )}
          onClick={() => setSelectedGoal(goal)}
        >
          <CardContent className="py-3 px-4">
            <div className="flex items-start gap-3">
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleGoalExpand(goal.id)
                  }}
                  className="mt-1"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              )}
              {!hasChildren && <span className="w-4" />}

              <CategoryIcon className={cn("h-5 w-5 mt-0.5", CATEGORY_COLORS[goal.category])} />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium truncate">{goal.title}</h4>
                  <Badge variant="outline" className={cn("text-xs", STATUS_COLORS[goal.status])}>
                    {goal.status}
                  </Badge>
                </div>

                {goal.description && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                    {goal.description}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2">
                  <div className="flex-1 min-w-[140px] max-w-[200px]">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span className="flex items-center gap-1">
                        Progress
                        {cardProgress?.is_manual && (
                          <span title="Manually set">✎</span>
                        )}
                      </span>
                      <span>{cardPct}%</span>
                    </div>
                    {/* Derived progress with the time-elapsed marker overlaid, so
                        "am I ahead or behind?" is answerable at a glance. */}
                    <div className="relative">
                      <Progress value={cardPct} className="h-1.5" />
                      {cardProgress && cardProgress.pacing.status !== 'no-target' && (
                        <span
                          className="absolute top-[-2px] h-[10px] w-[2px] bg-foreground/50 rounded"
                          style={{ left: `calc(${cardProgress.pacing.elapsed_pct}% - 1px)` }}
                          title={`${cardProgress.pacing.elapsed_pct}% of the time has elapsed`}
                        />
                      )}
                    </div>
                  </div>

                  {cardProgress && cardProgress.pacing.status !== 'no-target' && (
                    <Badge variant="outline" className={cn('text-xs shrink-0', PACING_COLORS[cardProgress.pacing.status])}>
                      {PACING_LABELS[cardProgress.pacing.status]}
                    </Badge>
                  )}

                  {goal.target_date && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
                      <Calendar className="h-3 w-3" />
                      {formatDate(goal.target_date)}
                      {cardProgress?.pacing.days_remaining !== null && cardProgress?.pacing.days_remaining !== undefined && (
                        <span className="hidden sm:inline">
                          ({cardProgress.pacing.days_remaining < 0
                            ? `${Math.abs(cardProgress.pacing.days_remaining)}d overdue`
                            : `${cardProgress.pacing.days_remaining}d left`})
                        </span>
                      )}
                    </span>
                  )}

                  {goal.linked_project_ids.length > 0 && (
                    <span className="hidden md:flex text-xs text-muted-foreground items-center gap-1 shrink-0">
                      <Link2 className="h-3 w-3" />
                      {goal.linked_project_ids.length} project(s)
                    </span>
                  )}
                </div>

                {/* Key results inline on the card — the countable targets are
                    usually the most concrete thing about a goal, so they're
                    worth seeing without opening the detail panel. */}
                {cardProgress && cardProgress.keyResults.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {cardProgress.keyResults.slice(0, 3).map((kr) => (
                      <Badge
                        key={kr.id}
                        variant="secondary"
                        className="text-xs font-normal max-w-full"
                        title={`${kr.label}: ${kr.current}/${kr.target}${kr.unit ? ' ' + kr.unit : ''}`}
                      >
                        <span className="truncate">{kr.label}</span>
                        <span className="ml-1 tabular-nums font-medium shrink-0">
                          {kr.current}/{kr.target}
                        </span>
                      </Badge>
                    ))}
                    {cardProgress.keyResults.length > 3 && (
                      <Badge variant="secondary" className="text-xs font-normal">
                        +{cardProgress.keyResults.length - 3}
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); startEdit(goal) }}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  {goal.status === 'active' && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange(goal, 'achieved') }}>
                      <Target className="h-4 w-4 mr-2" />
                      Mark Achieved
                    </DropdownMenuItem>
                  )}
                  {goal.status === 'active' && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange(goal, 'abandoned') }}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Abandon
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={(e) => { e.stopPropagation(); handleDeleteGoal(goal) }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardContent>
        </Card>

        {hasChildren && isExpanded && (
          <div className="border-l-2 border-muted pl-2">
            {childGoals.map((child) => renderGoalCard(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  const renderHorizonSection = (horizon: TimeHorizon, goalsInHorizon: Goal[]) => {
    const isExpanded = expandedHorizons.has(horizon)
    const topLevelGoals = goalsInHorizon.filter((g) => !g.parent_goal_id)
    // Goals in this horizon that are sub-goals of something else. They're
    // rendered nested under their parent (often in a different horizon
    // section), so listing them here too would duplicate them — but the count
    // badge used to include them, which made "Quarterly Goals (3)" sit above
    // "No quarterly goals yet". Call them out instead.
    const nestedCount = goalsInHorizon.length - topLevelGoals.length

    return (
      <div key={horizon} className="space-y-3">
        <button
          onClick={() => toggleHorizon(horizon)}
          className="flex items-center gap-2 w-full text-left group"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <h3 className="text-base md:text-lg font-semibold">{HORIZON_LABELS[horizon]} Goals</h3>
          <Badge variant="secondary" className="ml-2">{topLevelGoals.length}</Badge>
        </button>

        {isExpanded && (
          <div className="space-y-2 ml-2 md:ml-6">
            {topLevelGoals.length === 0 && nestedCount === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                No {horizon} goals yet. Create one to get started!
              </p>
            ) : (
              topLevelGoals.map((goal) => renderGoalCard(goal))
            )}
            {nestedCount > 0 && (
              <p className="text-xs text-muted-foreground py-1">
                + {nestedCount} {horizon} sub-goal{nestedCount === 1 ? '' : 's'} shown under {nestedCount === 1 ? 'its' : 'their'} parent goal{nestedCount === 1 ? '' : 's'}
              </p>
            )}
          </div>
        )}
      </div>
    )
  }

  // The goal detail panel. Rendered as a sticky side panel on desktop and as a
  // bottom sheet on phones — appending it below three horizon sections (the old
  // behaviour) put it off-screen, so tapping a goal appeared to do nothing.
  const renderGoalDetailBody = (selectedGoal: Goal) => (
    <>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {(() => {
                    const CategoryIcon = CATEGORY_ICONS[selectedGoal.category]
                    return <CategoryIcon className={cn("h-5 w-5", CATEGORY_COLORS[selectedGoal.category])} />
                  })()}
                  <CardTitle>{selectedGoal.title}</CardTitle>
                </div>
                <Badge variant="outline" className={cn(STATUS_COLORS[selectedGoal.status])}>
                  {selectedGoal.status}
                </Badge>
              </div>
              {selectedGoal.description && (
                <p className="text-sm text-muted-foreground">{selectedGoal.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Headline progress + pacing */}
              {goalProgress && (
                <div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-medium">
                      Overall Progress
                      {goalProgress.is_manual && (
                        <span className="ml-1.5 text-xs font-normal text-muted-foreground">(set manually)</span>
                      )}
                    </span>
                    <span className="text-2xl font-bold">{goalProgress.progress}%</span>
                  </div>
                  <div className="relative mt-1.5">
                    <Progress value={goalProgress.progress} className="h-2" />
                    {goalProgress.pacing.status !== 'no-target' && (
                      <span
                        className="absolute top-[-3px] h-[14px] w-[2px] bg-foreground/60 rounded"
                        style={{ left: `calc(${goalProgress.pacing.elapsed_pct}% - 1px)` }}
                        title={`${goalProgress.pacing.elapsed_pct}% of the time has elapsed`}
                      />
                    )}
                  </div>
                  {goalProgress.pacing.status !== 'no-target' && (
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                      <Badge variant="outline" className={cn(PACING_COLORS[goalProgress.pacing.status])}>
                        {PACING_LABELS[goalProgress.pacing.status]}
                      </Badge>
                      <span className="text-muted-foreground">
                        {goalProgress.pacing.elapsed_pct}% of the time elapsed
                        {goalProgress.pacing.days_remaining !== null && (
                          <> · {goalProgress.pacing.days_remaining < 0
                            ? `${Math.abs(goalProgress.pacing.days_remaining)} days overdue`
                            : `${goalProgress.pacing.days_remaining} days left`}</>
                        )}
                      </span>
                    </div>
                  )}
                  {goalProgress.is_manual && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Rolled-up value would be {
                        goalProgress.contributors.length > 0
                          ? Math.round(goalProgress.contributors.reduce((s, c) => s + c.progress, 0) / goalProgress.contributors.length)
                          : 0
                      }%. Clear the override in Edit to use it.
                    </p>
                  )}
                </div>
              )}

              {/* Progress Breakdown */}
              {goalProgress && (
                <div>
                  <span className="text-sm font-medium">Breakdown</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {goalProgress.total_tasks > 0 && (
                      <div className="flex items-center justify-between text-sm p-2 bg-accent/30 rounded">
                        <span className="text-muted-foreground">Tasks</span>
                        <span className="font-medium">{goalProgress.completed_tasks}/{goalProgress.total_tasks}</span>
                      </div>
                    )}
                    {goalProgress.projects.length > 0 && (
                      <div className="flex items-center justify-between text-sm p-2 bg-accent/30 rounded">
                        <span className="text-muted-foreground">Projects</span>
                        <span className="font-medium">{goalProgress.projects.filter(p => p.progress === 100).length}/{goalProgress.projects.length} done</span>
                      </div>
                    )}
                    {goalProgress.habits.length > 0 && (
                      <div className="flex items-center justify-between text-sm p-2 bg-green-500/10 rounded">
                        <span className="text-muted-foreground">Habit adherence</span>
                        <span className="font-medium text-green-500">
                          {Math.round(goalProgress.habits.reduce((s, h) => s + h.adherence, 0) / goalProgress.habits.length)}%
                        </span>
                      </div>
                    )}
                    {goalProgress.subGoals.length > 0 && (
                      <div className="flex items-center justify-between text-sm p-2 bg-accent/30 rounded">
                        <span className="text-muted-foreground">Sub-goals</span>
                        <span className="font-medium">{goalProgress.subGoals.filter(g => g.status === 'achieved').length}/{goalProgress.subGoals.length} achieved</span>
                      </div>
                    )}
                  </div>
                  {goalProgress.contributors.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Nothing linked yet — link a project, task, or habit (or add
                      sub-goals) and progress will roll up automatically.
                    </p>
                  )}
                </div>
              )}

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Time Horizon</span>
                  <p className="font-medium">{HORIZON_LABELS[selectedGoal.time_horizon]}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Category</span>
                  <p className="font-medium capitalize">{selectedGoal.category}</p>
                </div>
                {selectedGoal.target_date && (
                  <div>
                    <span className="text-muted-foreground">Target Date</span>
                    <p className="font-medium">{formatDate(selectedGoal.target_date)}</p>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Created</span>
                  <p className="font-medium">{formatDate(selectedGoal.created_at)}</p>
                </div>
                {selectedGoal.achieved_at && (
                  <div>
                    <span className="text-muted-foreground">Achieved</span>
                    <p className="font-medium">{formatDate(selectedGoal.achieved_at)}</p>
                  </div>
                )}
              </div>

              {/* Key Results — countable targets. These state the goal's own
                  number ("2 projects") instead of leaning on a proxy like the
                  task list of some linked project. */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Key Results</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => { setKrGoalId(selectedGoal.id); setKrLabel(''); setKrTarget('1'); setKrUnit('') }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />Add
                  </Button>
                </div>

                {(goalProgress?.keyResults.length ?? 0) === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    None yet. Add one for anything countable — "2 cross-team projects
                    led", "24 books read" — and it feeds the percentage above.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {goalProgress!.keyResults.map((kr) => (
                      <div key={kr.id} className="p-2 rounded bg-muted/30 space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm flex-1 min-w-0 break-words">{kr.label}</span>
                          <Button
                            variant="ghost" size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            title="Remove key result"
                            onClick={() => deleteKeyResult(selectedGoal.id, kr.id)}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* One-tap steppers: on a phone this is how you move a
                              key result, rather than opening a form to type. */}
                          <Button
                            variant="outline" size="sm"
                            className="h-8 w-8 p-0 shrink-0"
                            aria-label={`Decrease ${kr.label}`}
                            disabled={kr.current <= 0}
                            onClick={() => stepKeyResult(selectedGoal.id, kr.id, -1)}
                          >
                            −
                          </Button>
                          <span className="text-sm font-medium tabular-nums w-16 text-center shrink-0">
                            {kr.current}/{kr.target}
                          </span>
                          <Button
                            variant="outline" size="sm"
                            className="h-8 w-8 p-0 shrink-0"
                            aria-label={`Increase ${kr.label}`}
                            onClick={() => stepKeyResult(selectedGoal.id, kr.id, 1)}
                          >
                            +
                          </Button>
                          <div className="flex-1 min-w-0">
                            <Progress value={kr.progress} className="h-1.5" />
                          </div>
                          <span className="text-xs text-muted-foreground w-9 text-right shrink-0">
                            {kr.progress}%
                          </span>
                        </div>
                        {kr.unit && (
                          <span className="text-xs text-muted-foreground">{kr.unit}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Check-ins — dated snapshots of the derived number plus a note.
                  Turns a single live percentage into a trend, and the notes are
                  the running record of what actually happened. */}
              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Check-ins</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => { setCheckInGoalId(selectedGoal.id); setCheckInNote('') }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />Check in
                  </Button>
                </div>

                {(selectedGoal.check_ins?.length ?? 0) === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No check-ins yet. Recording one captures today's {goalProgress?.progress ?? 0}%
                    with a note, so you build a trend and a record over the year.
                  </p>
                ) : (
                  <>
                    <ProgressSparkline checkIns={selectedGoal.check_ins!} />
                    <div className="space-y-1">
                      {[...selectedGoal.check_ins!].reverse().slice(0, 6).map((ci) => (
                        <div key={ci.id} className="flex items-start gap-2 p-2 rounded bg-muted/30">
                          <Badge variant="outline" className="text-xs shrink-0 tabular-nums">
                            {ci.progress}%
                          </Badge>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-muted-foreground">{formatDate(ci.date)}</span>
                            {ci.note && (
                              <p className="text-sm break-words whitespace-pre-wrap">{ci.note}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost" size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            title="Delete check-in"
                            onClick={() => deleteCheckIn(selectedGoal.id, ci.id)}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Attached notes / journal entries / lists — managed from this
                  side too: the picker writes to the other object's
                  linked_goal_ids, so links are two-way editable, any time. */}
              <AttachedItemsSection targetType="goal" targetId={selectedGoal.id} />

              {/* Linked Items Section with Tabs */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Linked Items</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setShowVisionBoard(true)}>
                      <Eye className="h-4 w-4 mr-1" />
                      Vision Board
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowLinkDialog(true)}>
                      <Link2 className="h-4 w-4 mr-1" />
                      Link
                    </Button>
                  </div>
                </div>

                {/* Sub-goals */}
                {goalProgress && goalProgress.subGoals.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Target className="h-3 w-3" />
                      <span>Sub-goals ({goalProgress.subGoals.length})</span>
                    </div>
                    <div className="space-y-1">
                      {goalProgress.subGoals.map((sg) => (
                        <div key={sg.goal_id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                          <div className="flex-1">
                            <p className="text-sm">{sg.goal_title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Progress value={sg.progress} className="h-1 flex-1 max-w-[80px]" />
                              <Badge variant="outline" className="text-xs">{sg.status}</Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Projects */}
                {goalProgress && goalProgress.projects.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <FolderKanban className="h-3 w-3" />
                      <span>Projects ({goalProgress.projects.length})</span>
                    </div>
                    <div className="space-y-1">
                      {goalProgress.projects.map((proj) => (
                        <div key={proj.project_id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                          <div className="flex-1">
                            <p className="text-sm">{proj.project_name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Progress value={proj.progress} className="h-1 flex-1 max-w-[80px]" />
                              <span className="text-xs text-muted-foreground">
                                {proj.completed_tasks}/{proj.total_tasks}
                              </span>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleUnlinkProject(proj.project_id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Habits */}
                {goalProgress && goalProgress.habits.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Repeat className="h-3 w-3" />
                      <span>Habits ({goalProgress.habits.length})</span>
                    </div>
                    <div className="space-y-1">
                      {goalProgress.habits.map((habit) => (
                        <div key={habit.habit_id} className="flex items-center justify-between gap-2 p-2 rounded bg-muted/30">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">{habit.habit_title}</p>
                            {/* Adherence is what this habit actually contributes
                                to the goal — previously only a raw tally showed,
                                and habits contributed 0 to the percentage. */}
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Progress value={habit.adherence} className="h-1 w-16" />
                              <span className="text-xs text-muted-foreground">
                                {habit.completion_count}/{habit.expected_count} · {habit.adherence}%
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground capitalize">{habit.frequency}</span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Badge variant={habit.is_active ? "default" : "secondary"} className="text-xs">
                              {habit.is_active ? 'Active' : 'Paused'}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => handleUnlinkHabit(habit.habit_id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Direct Tasks */}
                {goalProgress && goalProgress.directTasks.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <CheckSquare className="h-3 w-3" />
                      <span>Tasks ({goalProgress.directTasks.length})</span>
                    </div>
                    <div className="space-y-1">
                      {goalProgress.directTasks.map((task) => (
                        <div key={task.task_id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                          <div className="flex-1">
                            <p className="text-sm">{task.task_title}</p>
                            {task.project_name && (
                              <span className="text-xs text-muted-foreground">{task.project_name}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Badge variant={task.status === 'completed' ? "default" : "secondary"} className="text-xs">
                              {task.status}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => handleUnlinkTask(task.task_id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {goalProgress && goalProgress.projects.length === 0 && goalProgress.habits.length === 0 && goalProgress.directTasks.length === 0 && goalProgress.subGoals.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No items linked yet. Link projects, habits, or tasks to track progress toward this goal.
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => startEdit(selectedGoal)}>
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                {selectedGoal.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange(selectedGoal, 'achieved')}
                  >
                    <TrendingUp className="h-4 w-4 mr-1" />
                    Mark Achieved
                  </Button>
                )}
              </div>
            </CardContent>
    </>
  )

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-lg md:text-2xl font-bold flex items-center gap-2">
              <Target className="h-6 w-6" />
              Goals
            </h2>
            <p className="text-sm text-muted-foreground">
              {activeGoals.length} active goals across {goals.length} total
            </p>
          </div>
        </div>
        <Button size="sm" onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Goal
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        {/* Goals List */}
        <div className="space-y-6 md:col-span-7">
          {renderHorizonSection('yearly', yearlyGoals)}
          {renderHorizonSection('quarterly', quarterlyGoals)}
          {renderHorizonSection('monthly', monthlyGoals)}
        </div>

        {/* Goal Details — desktop side panel. On phones the same content opens
            as a bottom sheet (below), since a fourth stacked section would sit
            well below the fold. */}
        <div className="hidden md:block md:col-span-5">
          {isPhone ? null : selectedGoal ? (
            <Card className="sticky top-4">
              {renderGoalDetailBody(selectedGoal)}
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a goal to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Goal Details — phone bottom sheet */}
      <Dialog
        open={!!selectedGoal && isPhone}
        onOpenChange={(open) => { if (!open) setSelectedGoal(null) }}
      >
        <DialogContent className="md:hidden max-h-[88dvh] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>{selectedGoal?.title ?? 'Goal'}</DialogTitle>
            <DialogDescription>Goal progress, breakdown, and linked items</DialogDescription>
          </DialogHeader>
          {selectedGoal && renderGoalDetailBody(selectedGoal)}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Goal Dialog */}
      <Dialog open={showCreateDialog || !!editingGoal} onOpenChange={(open) => {
        if (!open) {
          resetForm()
          setShowCreateDialog(false)
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGoal ? 'Edit Goal' : 'Create New Goal'}</DialogTitle>
            <DialogDescription className="sr-only">
              {editingGoal ? 'Edit the goal details' : 'Create a new goal with title, description, time horizon, and category'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Goal title..."
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="What do you want to achieve?"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Time Horizon</Label>
                <Select value={formHorizon} onValueChange={(v) => setFormHorizon(v as TimeHorizon)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formCategory} onValueChange={(v) => setFormCategory(v as GoalCategory)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="career">Career</SelectItem>
                    <SelectItem value="health">Health</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="learning">Learning</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                    <SelectItem value="relationships">Relationships</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Target Date (optional)</Label>
              <Input
                type="date"
                value={formTargetDate}
                onChange={(e) => setFormTargetDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Parent Goal (optional)</Label>
              <Select value={formParentId} onValueChange={setFormParentId}>
                <SelectTrigger>
                  <SelectValue placeholder="No parent (top-level goal)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No parent</SelectItem>
                  {goals
                    .filter((g) => g.id !== editingGoal?.id)
                    .map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Manual override. Progress is normally rolled up from linked
                work; this is the escape hatch for goals that can't be
                measured that way (and it's why the legacy `progress` field
                isn't reused — `progress: 0` would be ambiguous). */}
            {editingGoal && (
              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center justify-between">
                  <Label>Progress</Label>
                  <span className="text-xs text-muted-foreground">
                    {formOverride === '' ? 'Auto (rolled up)' : `${formOverride}% (manual)`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    inputMode="numeric"
                    placeholder="Auto"
                    value={formOverride}
                    onChange={(e) => setFormOverride(e.target.value)}
                    className="w-24"
                  />
                  {formOverride !== '' && (
                    <Button variant="ghost" size="sm" onClick={() => setFormOverride('')}>
                      Use auto
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave blank to roll up automatically from sub-goals, projects,
                  tasks, and habit adherence.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowCreateDialog(false) }}>
              Cancel
            </Button>
            <Button onClick={editingGoal ? handleUpdateGoal : handleCreateGoal} disabled={!formTitle.trim()}>
              {editingGoal ? 'Save Changes' : 'Create Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Key Result */}
      <Dialog open={!!krGoalId} onOpenChange={(open) => { if (!open) setKrGoalId(null) }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Key Result</DialogTitle>
            <DialogDescription>
              A countable target. Progress on it rolls up into the goal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>What are you counting?</Label>
              <Input
                autoFocus
                placeholder="e.g. Cross-team projects led"
                value={krLabel}
                onChange={(e) => setKrLabel(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label>Target</Label>
                <Input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={krTarget}
                  onChange={(e) => setKrTarget(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Unit (optional)</Label>
                <Input
                  placeholder="projects"
                  value={krUnit}
                  onChange={(e) => setKrUnit(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKrGoalId(null)}>Cancel</Button>
            <Button
              disabled={!krLabel.trim() || !(Number(krTarget) >= 1)}
              onClick={async () => {
                if (!krGoalId) return
                await addKeyResult(krGoalId, krLabel.trim(), Number(krTarget), krUnit.trim() || null)
                setKrGoalId(null)
              }}
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record a check-in */}
      <Dialog open={!!checkInGoalId} onOpenChange={(open) => { if (!open) setCheckInGoalId(null) }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Check in</DialogTitle>
            <DialogDescription>
              Records today at {checkInGoalId ? progressById[checkInGoalId]?.progress ?? 0 : 0}% — the
              current rolled-up figure — with a note for context.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Note (optional)</Label>
            <Textarea
              autoFocus
              rows={4}
              placeholder="What moved, what's blocked, what you heard…"
              value={checkInNote}
              onChange={(e) => setCheckInNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckInGoalId(null)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!checkInGoalId) return
                await addCheckIn(checkInGoalId, checkInNote)
                setCheckInGoalId(null)
              }}
            >
              Save check-in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Items Dialog with Tabs */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link Items to Goal</DialogTitle>
            <DialogDescription className="sr-only">
              Link projects, habits, or tasks to track progress toward this goal
            </DialogDescription>
          </DialogHeader>
          <Tabs value={linkDialogTab} onValueChange={(v) => setLinkDialogTab(v as typeof linkDialogTab)} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="projects" className="flex items-center gap-1">
                <FolderKanban className="h-4 w-4" />
                Projects
              </TabsTrigger>
              <TabsTrigger value="habits" className="flex items-center gap-1">
                <Repeat className="h-4 w-4" />
                Habits
              </TabsTrigger>
              <TabsTrigger value="tasks" className="flex items-center gap-1">
                <CheckSquare className="h-4 w-4" />
                Tasks
              </TabsTrigger>
            </TabsList>

            {/* Projects Tab */}
            <TabsContent value="projects" className="mt-4">
              {activeProjects.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No active projects available
                </p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {activeProjects.map((project) => {
                    const isLinked = selectedGoal?.linked_project_ids.includes(project.id)
                    return (
                      <button
                        key={project.id}
                        onClick={() => {
                          if (isLinked) {
                            handleUnlinkProject(project.id)
                          } else {
                            handleLinkProject(project.id)
                          }
                        }}
                        className={cn(
                          "w-full text-left p-3 rounded border transition-colors",
                          isLinked ? "bg-primary/10 border-primary" : "hover:bg-accent"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{project.name}</span>
                          {isLinked && <Badge variant="secondary">Linked</Badge>}
                        </div>
                        {project.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                            {project.description}
                          </p>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </TabsContent>

            {/* Habits Tab */}
            <TabsContent value="habits" className="mt-4">
              {habits.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No habits available
                </p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {habits.map((habit) => {
                    const linkedHabitIds = selectedGoal?.linked_habit_ids ?? []
                    const isLinked = linkedHabitIds.includes(habit.id)
                    return (
                      <button
                        key={habit.id}
                        onClick={() => {
                          if (isLinked) {
                            handleUnlinkHabit(habit.id)
                          } else {
                            handleLinkHabit(habit.id)
                          }
                        }}
                        className={cn(
                          "w-full text-left p-3 rounded border transition-colors",
                          isLinked ? "bg-primary/10 border-primary" : "hover:bg-accent"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{habit.title}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs capitalize">
                              {habit.recurrence?.frequency || 'daily'}
                            </Badge>
                            {isLinked && <Badge variant="secondary">Linked</Badge>}
                          </div>
                        </div>
                        {habit.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                            {habit.description}
                          </p>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </TabsContent>

            {/* Tasks Tab */}
            <TabsContent value="tasks" className="mt-4">
              {regularTasks.filter(t => t.status !== 'completed').length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No active tasks available
                </p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {regularTasks.filter(t => t.status !== 'completed').map((task) => {
                    const linkedTaskIds = selectedGoal?.linked_task_ids ?? []
                    const isLinked = linkedTaskIds.includes(task.id)
                    return (
                      <button
                        key={task.id}
                        onClick={() => {
                          if (isLinked) {
                            handleUnlinkTask(task.id)
                          } else {
                            handleLinkTask(task.id)
                          }
                        }}
                        className={cn(
                          "w-full text-left p-3 rounded border transition-colors",
                          isLinked ? "bg-primary/10 border-primary" : "hover:bg-accent"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{task.title}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {task.status}
                            </Badge>
                            {isLinked && <Badge variant="secondary">Linked</Badge>}
                          </div>
                        </div>
                        {task.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                            {task.description}
                          </p>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vision Board Dialog */}
      <Dialog open={showVisionBoard} onOpenChange={setShowVisionBoard}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Vision Board: {selectedGoal?.title}
            </DialogTitle>
            <DialogDescription className="sr-only">
              View goal progress breakdown including projects, habits, and tasks
            </DialogDescription>
          </DialogHeader>
          {selectedGoal && goalProgress && (
            <GoalVisionBoard goal={selectedGoal} progress={goalProgress} goals={goals} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVisionBoard(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Vision Board Component - exported for use in sidebar widget
export interface GoalVisionBoardProps {
  goal: Goal
  progress: GoalProgress
  goals: Goal[]
}

type FilterType = 'all' | 'subgoals' | 'projects' | 'habits' | 'tasks'

interface HabitCompletionData {
  habit_id: string
  completions: HabitInstance[]
  streak: number
}

export { HORIZON_LABELS, STATUS_COLORS }

/** The goal's current period so far: this month, quarter or year, to today. */
function horizonRange(horizon: TimeHorizon, today: string): { start: string; end: string } {
  const [y, m] = today.split('-').map(Number)
  const month = horizon === 'yearly' ? 1 : horizon === 'quarterly' ? Math.floor((m - 1) / 3) * 3 + 1 : m
  return { start: `${y}-${String(month).padStart(2, '0')}-01`, end: today }
}

export function GoalVisionBoard({ goal, progress, goals }: GoalVisionBoardProps) {
  const [filter, setFilter] = useState<FilterType>('all')
  const { tasks, habitInstances } = useApp()
  const todayKey = useToday()

  // Completions in the goal's horizon, and the habit's own streak — the one
  // its page shows (and, for an active habit, the sidebar). This used to
  // count back calendar days, so a Mon/Wed/Fri habit's streak stopped at the
  // first Tuesday.
  const habitCompletions = useMemo(() => {
    const today = todayKey()
    const { start, end } = horizonRange(goal.time_horizon, today)
    const map: Record<string, HabitCompletionData> = {}
    for (const habit of progress.habits) {
      const task = tasks.find((t) => t.id === habit.habit_id)
      map[habit.habit_id] = {
        habit_id: habit.habit_id,
        completions: habitInstancesFor(habitInstances, habit.habit_id, start, end),
        // The habit page's record, so an archived habit reads the same here.
        streak: task ? habitRecord(task, habitInstances, today, today, today, task.id).currentStreak : 0,
      }
    }
    return map
  }, [progress.habits, habitInstances, tasks, goal.time_horizon, todayKey])

  // Get parent goal chain
  const getParentChain = (goalId: string): Goal[] => {
    const result: Goal[] = []
    let currentGoal = goals.find(g => g.id === goalId)
    while (currentGoal?.parent_goal_id) {
      const parent = goals.find(g => g.id === currentGoal!.parent_goal_id)
      if (parent) {
        result.unshift(parent)
        currentGoal = parent
      } else {
        break
      }
    }
    return result
  }

  const parentChain = getParentChain(goal.id)
  const hasSubGoals = progress.subGoals.length > 0
  const hasProjects = progress.projects.length > 0
  const hasHabits = progress.habits.length > 0
  const hasTasks = progress.directTasks.length > 0
  const hasAnyItems = hasSubGoals || hasProjects || hasHabits || hasTasks

  const showSubGoals = filter === 'all' || filter === 'subgoals'
  const showProjects = filter === 'all' || filter === 'projects'
  const showHabits = filter === 'all' || filter === 'habits'
  const showTasks = filter === 'all' || filter === 'tasks'

  const horizonLabel = HORIZON_LABELS[goal.time_horizon]

  // Count visible columns for grid layout
  const visibleColumns = [
    showSubGoals && hasSubGoals,
    showProjects && hasProjects,
    showHabits && hasHabits,
    showTasks && hasTasks,
  ].filter(Boolean).length

  return (
    <div className="space-y-6 py-4">
      {/* Central Goal Card */}
      <div className="flex justify-center">
        <div className="relative">
          {/* Connecting lines radiating down */}
          {hasAnyItems && (
            <div className="absolute left-1/2 top-full w-px h-8 bg-gradient-to-b from-primary/50 to-transparent" />
          )}

          {/* Main Goal Card */}
          <div className="relative p-6 rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent shadow-lg shadow-primary/10 min-w-[320px] max-w-md">
            {/* Parent breadcrumb */}
            {parentChain.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                {parentChain.map((parent, idx) => (
                  <span key={parent.id} className="flex items-center gap-1">
                    {idx > 0 && <ChevronRight className="h-3 w-3" />}
                    <span className="truncate max-w-[100px]">{parent.title}</span>
                  </span>
                ))}
                <ChevronRight className="h-3 w-3" />
              </div>
            )}

            {/* Goal icon and title */}
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-primary/20 shrink-0">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold mb-1">{goal.title}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-semibold text-primary">{Math.round(progress.overall_progress)}%</span>
                  <span>•</span>
                  <span>{progress.completed_tasks}/{progress.total_tasks} tasks</span>
                  <span>•</span>
                  <span>{horizonLabel}</span>
                </div>
              </div>
              <Badge className={cn(STATUS_COLORS[goal.status], "shrink-0")}>{goal.status}</Badge>
            </div>

            {/* Progress bar */}
            <div className="mt-4">
              <Progress value={progress.overall_progress} className="h-2" />
            </div>

            {/* Filter buttons */}
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
                className="rounded-full h-7 text-xs"
              >
                All
              </Button>
              {hasSubGoals && (
                <Button
                  variant={filter === 'subgoals' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter('subgoals')}
                  className="rounded-full h-7 text-xs bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 text-purple-500"
                >
                  <Target className="h-3 w-3 mr-1" />
                  {progress.subGoals.length}
                </Button>
              )}
              {hasProjects && (
                <Button
                  variant={filter === 'projects' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter('projects')}
                  className="rounded-full h-7 text-xs bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20 text-blue-500"
                >
                  <FolderKanban className="h-3 w-3 mr-1" />
                  {progress.projects.length}
                </Button>
              )}
              {hasHabits && (
                <Button
                  variant={filter === 'habits' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter('habits')}
                  className="rounded-full h-7 text-xs bg-green-500/10 border-green-500/30 hover:bg-green-500/20 text-green-500"
                >
                  <Repeat className="h-3 w-3 mr-1" />
                  {progress.habits.length}
                </Button>
              )}
              {hasTasks && (
                <Button
                  variant={filter === 'tasks' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter('tasks')}
                  className="rounded-full h-7 text-xs bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20 text-amber-500"
                >
                  <CheckSquare className="h-3 w-3 mr-1" />
                  {progress.directTasks.length}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Vertical columns for each category */}
      {hasAnyItems ? (
        <div className={cn(
          "grid gap-4",
          visibleColumns === 1 && "grid-cols-1 max-w-sm mx-auto",
          visibleColumns === 2 && "grid-cols-2",
          visibleColumns === 3 && "grid-cols-3",
          visibleColumns >= 4 && "grid-cols-4"
        )}>
          {/* Sub-goals Column */}
          {showSubGoals && hasSubGoals && (
            <div className="space-y-3">
              {/* Column header with connecting line */}
              <div className="flex flex-col items-center">
                <div className="w-px h-4 bg-gradient-to-b from-primary/30 to-purple-500/50" />
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/20 border border-purple-500/30">
                  <Target className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-semibold text-purple-500">Sub-goals</span>
                </div>
              </div>
              {/* Cards */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {progress.subGoals.map((sg) => (
                  <div
                    key={sg.goal_id}
                    className="p-3 rounded-lg border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-purple-500/5 hover:border-purple-500/50 transition-all hover:shadow-md hover:shadow-purple-500/10"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 rounded-full bg-purple-500/20">
                        <Target className="h-3.5 w-3.5 text-purple-500" />
                      </div>
                      <span className="text-sm font-medium truncate flex-1">{sg.goal_title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={sg.progress} className="h-1.5 flex-1" />
                      <span className="text-xs font-bold text-purple-500">{Math.round(sg.progress)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Projects Column */}
          {showProjects && hasProjects && (
            <div className="space-y-3">
              {/* Column header with connecting line */}
              <div className="flex flex-col items-center">
                <div className="w-px h-4 bg-gradient-to-b from-primary/30 to-blue-500/50" />
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 border border-blue-500/30">
                  <FolderKanban className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-semibold text-blue-500">Projects</span>
                </div>
              </div>
              {/* Cards */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {progress.projects.map((proj) => (
                  <div
                    key={proj.project_id}
                    className="p-3 rounded-lg border-2 border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-blue-500/5 hover:border-blue-500/50 transition-all hover:shadow-md hover:shadow-blue-500/10"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 rounded-md bg-blue-500/20">
                        <FolderKanban className="h-3.5 w-3.5 text-blue-500" />
                      </div>
                      <span className="text-sm font-semibold truncate flex-1">{proj.project_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={proj.progress} className="h-1.5 flex-1" />
                      <span className="text-xs font-bold text-blue-500">{Math.round(proj.progress)}%</span>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <CheckSquare className="h-3 w-3" />
                      <span>{proj.completed_tasks}/{proj.total_tasks}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Habits Column */}
          {showHabits && hasHabits && (
            <div className="space-y-3">
              {/* Column header with connecting line */}
              <div className="flex flex-col items-center">
                <div className="w-px h-4 bg-gradient-to-b from-primary/30 to-green-500/50" />
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/30">
                  <Repeat className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-semibold text-green-500">Habits</span>
                </div>
              </div>
              {/* Cards */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {progress.habits.map((habit) => {
                  const completionData = habitCompletions[habit.habit_id]
                  const completionCount = completionData?.completions.length ?? 0
                  const streak = completionData?.streak ?? 0

                  return (
                    <div
                      key={habit.habit_id}
                      className="p-3 rounded-lg border-2 border-green-500/30 bg-gradient-to-br from-green-500/10 to-green-500/5 hover:border-green-500/50 transition-all hover:shadow-md hover:shadow-green-500/10"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 rounded-full bg-green-500/20">
                          <Repeat className="h-3.5 w-3.5 text-green-500" />
                        </div>
                        <span className="text-sm font-medium truncate flex-1">{habit.habit_title}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mb-2">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-green-500/30">{habit.frequency}</Badge>
                        <Badge variant={habit.is_active ? "default" : "secondary"} className="text-[10px] px-1.5 py-0 h-5">
                          {habit.is_active ? 'Active' : 'Paused'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <div className="flex items-center gap-1 text-green-600">
                          <Circle className="h-3 w-3 fill-green-500 text-green-500" />
                          <span className="font-medium">{completionCount}×</span>
                        </div>
                        {streak > 0 && (
                          <div className="flex items-center gap-1 text-orange-500">
                            <Flame className="h-3 w-3" />
                            <span className="font-medium">{streak}d streak</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Tasks Column */}
          {showTasks && hasTasks && (
            <div className="space-y-3">
              {/* Column header with connecting line */}
              <div className="flex flex-col items-center">
                <div className="w-px h-4 bg-gradient-to-b from-primary/30 to-amber-500/50" />
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30">
                  <CheckSquare className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-semibold text-amber-500">Tasks</span>
                </div>
              </div>
              {/* Cards */}
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                {progress.directTasks.map((task) => (
                  <div
                    key={task.task_id}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all",
                      task.status === 'completed'
                        ? "bg-green-500/10 text-green-600 border border-green-500/30 line-through opacity-60"
                        : task.status === 'in_progress'
                        ? "bg-amber-500/15 text-foreground border-2 border-amber-500/40 shadow-sm"
                        : "bg-amber-500/5 text-foreground border border-amber-500/20 hover:border-amber-500/40"
                    )}
                  >
                    <CheckSquare className={cn(
                      "h-4 w-4 shrink-0",
                      task.status === 'completed' ? "text-green-500" : "text-amber-500"
                    )} />
                    <span className="truncate">{task.task_title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
            <Target className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <p className="text-muted-foreground">No items linked to this goal yet</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Link projects, habits, or tasks to track progress</p>
        </div>
      )}
    </div>
  )
}
